import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';
import { alertController, getPlatforms } from '@ionic/core';
import { useEventBus } from '@vueuse/core';
import type { ComputedRef } from 'vue';
import { computed, reactive } from 'vue';
import { isPlatform } from '@ionic/vue';
import packageJson from '../../package.json';
import { clearStorage, isNativeMobile } from './helper';
import { openSupportModal, openUserSubscribeSettingsModal } from './modalComponents';

import {
  ActivityTypeEnum,
  AppMenuLanguagesEnum,
  EventBusEnum,
  InterfaceSizeAppEnum,
  PagesBackgroundEnum,
  SendKeyEnum,
  SettingsActionEnum,
  SettingsTypeEnum,
  ThemeAppEnum,
  UserRoleEnum,
  AppInfoListEnum,
  AppIconsEnum,
} from '@/enums';
import { useAppBar, useImages, useLoading, useErrors, DateHelper } from '@/helpers';
import { SUPPORT_LOCALES, loadLocaleMessages, useI18n } from '@/i18n';
import router, { ROUTES_NAME } from '@/router';
import axiosInstance from '@/services/axios';
import { calculateAllStoresSize, useAppStore, useAuthStore, useNetworkStore, useUserStore } from '@/store';
import type {
  FormSelectDataModel,
  NetworkModel,
  NotificationsSettingsItemModel,
  NotificationsSettingsModel,
  SettingsItemModel,
  UserGroupShortModel,
  UserSubscribeSettingsRequestModel,
  NetworkFullSettingsModel,
} from '@/types';

type IUseSettings = {
  getSettingsItems(): SettingsItemModel[];
  settingParamsAction(
    type: SettingsTypeEnum,
    value: boolean | ThemeAppEnum | PagesBackgroundEnum | AppMenuLanguagesEnum | SendKeyEnum | InterfaceSizeAppEnum,
    currentNetworkId: string
  ): Promise<void>;
  settingButtonsAction(action: SettingsActionEnum): Promise<void>;
  showAppInfo(): Promise<void>;
  notificationsSettings(): {
    notificationsForAdmins: NotificationsSettingsModel;
    chooseWhereToSend: NotificationsSettingsModel;
    notifyMeIf: NotificationsSettingsModel;
    activityType: FormSelectDataModel;
    groups: {
      title: string;
      items: { enable: boolean; group: UserGroupShortModel }[];
    };
  };
};

export const useSettings = (): IUseSettings => {
  const { t } = useI18n();
  const clearCache = async () => {
    const { handleError } = useErrors();
    const loadingHelper = useLoading();

    const clearStorageAlert = await alertController.create({
      message: t('cache.clearConfirm'),
      buttons: [
        {
          text: t('cancel'),
          role: 'cancel',
          cssClass: 'custom-alert-buttons',
        },
        {
          text: t('confirm'),
          cssClass: 'custom-alert-buttons',
          handler: async () => {
            const { emit: emitResetAppBarSearch } = useEventBus(EventBusEnum.ResetAppBarSearch);
            clearStorageAlert.dismiss();
            await loadingHelper.create(t('loading'));

            try {
              await router.push({ name: ROUTES_NAME.LOADING });

              await Promise.race([
                clearStorage(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 30000)),
                emitResetAppBarSearch(),
                useAppBar().resetAllFilters(),
              ]);
            } catch (e: any) {
              handleError({
                show: true,
                error: undefined,
                message: `Error occurred: ${e.message}`,
              });
            } finally {
              loadingHelper.dismiss();
            }
          },
        },
      ],
    });

    const clearImagesCacheAlert = await alertController.create({
      message: t('cache.clearImagesCacheConfirm', {
        count: useImages().count,
        size: (useImages().size / (1024 * 1024)).toFixed(2),
      }),
      buttons: [
        {
          text: t('cancel'),
          role: 'cancel',
          cssClass: 'custom-alert-buttons',
          handler: async () => {
            clearImagesCacheAlert.dismiss();
            await clearStorageAlert.present();
          },
        },
        {
          text: t('confirm'),
          cssClass: 'custom-alert-buttons',
          handler: async () => {
            clearImagesCacheAlert.dismiss();
            await loadingHelper.create(t('loading'));

            try {
              useImages().clearImages();
            } catch (e: any) {
              handleError({
                show: true,
                error: undefined,
                message: `Error occurred: ${e.message}`,
              });
            } finally {
              loadingHelper.dismiss();
              await clearStorageAlert.present();
            }
          },
        },
      ],
    });

    await clearImagesCacheAlert.present();
  };

  const getSettingsItems = () => {
    const appStore = useAppStore();
    const networkStore = useNetworkStore();
    const userStore = useUserStore();

    const currentTheme: ComputedRef<ThemeAppEnum> = computed(() => appStore.theme);

    const pagesBackground: ComputedRef<PagesBackgroundEnum> = computed(() => appStore.pagesBackground);
    const notificationsIsActive: ComputedRef<boolean> = computed(() => appStore.localNotifications);
    const language: ComputedRef<string> = computed(() => appStore.locale);
    const canPostOnBehalfMe: ComputedRef<boolean> = computed(() => appStore.canPostOnBehalfMe);
    const allowAddMeToGroups: ComputedRef<boolean> = computed(() => userStore.current?.allowAddMeToGroups || false);
    const appSendKey: ComputedRef<SendKeyEnum> = computed(() => appStore.sendKey);
    const currentNetwork: ComputedRef<NetworkModel> = computed(() => networkStore.getNetwork);

    const currentNetworkSettings: ComputedRef<NetworkFullSettingsModel | null> = computed(
      () => networkStore.getCurrentSettings
    );

    const isPostOnBehalfAvailable: ComputedRef<boolean> = computed(
      () => currentNetworkSettings.value?.allowPostOnBehalf || false
    );

    const interfaceSize: ComputedRef<InterfaceSizeAppEnum> = computed(() => appStore.interfaceSize);

    const isSMWidth: ComputedRef<boolean> = computed(() => appStore.isSMWidth);
    const isMDWidth: ComputedRef<boolean> = computed(() => appStore.isMDWidth);
    const isXSWidth: ComputedRef<boolean> = computed(() => appStore.isXSWidth);

    const getItemSize = (defaultSize: number | 'auto'): number | 'auto' => {
      if (isMDWidth.value) return defaultSize;

      if (isSMWidth.value) return 2;

      if (isXSWidth.value) return 4;

      return defaultSize;
    };

    const items: SettingsItemModel[] = [
      {
        title: t('appMenu.theme.title'),
        subTitle: null,
        icon: AppIconsEnum.Appearance,
        type: SettingsTypeEnum.Theme,
        params: {
          value: currentTheme.value,
          options: [
            {
              title: t('appMenu.theme.light'),
              value: ThemeAppEnum.Light,
            },
            {
              title: t('appMenu.theme.dark'),
              value: ThemeAppEnum.Dark,
            },
            {
              title: t('appMenu.theme.system'),
              value: ThemeAppEnum.System,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: true,
      },

      {
        title: t('appMenu.language'),
        subTitle: null,
        icon: AppIconsEnum.Earth,
        type: SettingsTypeEnum.Lang,
        params: {
          value: language.value,
          options: [
            {
              title: 'Deutsch',
              value: AppMenuLanguagesEnum.DE,
            },
            {
              title: 'English',
              value: AppMenuLanguagesEnum.EN,
            },
            {
              title: 'Czech',
              value: AppMenuLanguagesEnum.CS,
            },
            {
              title: 'Spanish',
              value: AppMenuLanguagesEnum.ES,
            },
            {
              title: 'French',
              value: AppMenuLanguagesEnum.FR,
            },
            {
              title: 'Russian',
              value: AppMenuLanguagesEnum.RU,
            },
          ].filter((n) => SUPPORT_LOCALES.includes(n.value)),
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: true,
      },

      {
        title: t('appMenu.pagesBackground'),
        subTitle: null,
        icon: AppIconsEnum.Media,
        type: SettingsTypeEnum.Background,
        params: {
          value: pagesBackground.value,
          options: [
            {
              title: t('on'),
              value: PagesBackgroundEnum.CustomImage,
            },
            {
              title: t('off'),
              value: PagesBackgroundEnum.Default,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: true,
      },

      {
        title: t('appMenu.sound'),
        subTitle: null,
        icon: AppIconsEnum.VolumeUp,
        type: SettingsTypeEnum.Sound,
        params: {
          value: notificationsIsActive.value,
          options: [
            {
              title: t('on'),
              value: true,
            },
            {
              title: t('off'),
              value: false,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: true,
      },

      {
        title: t('settings.notifications.setup'),
        subTitle: '',
        icon: AppIconsEnum.Notifications,
        type: SettingsTypeEnum.Notifications,
        params: null,
        textContent: '',
        actions: {
          leftBtn: {
            title: '',
            action: SettingsActionEnum.None,
          },
          rightBtn: {
            title: t('appMenu.settings'),
            action: SettingsActionEnum.OpenNotifications,
          },
        },
        size: getItemSize(1),
        enabled: true,
      },

      {
        title: t('appMenu.sending'),
        subTitle: null,
        icon: AppIconsEnum.SoftWrap,
        type: SettingsTypeEnum.Sending,
        params: {
          value: appSendKey.value,
          options: [
            {
              title: t('settings.enter'),
              value: SendKeyEnum.Enter,
            },
            {
              title: t('settings.ctrlEnter'),
              value: SendKeyEnum.CtrlEnter,
            },
            {
              title: t('settings.cmdEnter'),
              value: SendKeyEnum.CmdEnter,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: !isNativeMobile,
      },

      {
        title: t('appMenu.onBehalf'),
        subTitle: null,
        icon: AppIconsEnum.PaperAirplane,
        type: SettingsTypeEnum.PostsOnBehalf,
        params: {
          value: canPostOnBehalfMe.value,
          options: [
            {
              title: t('on'),
              value: true,
            },
            {
              title: t('off'),
              value: false,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(2),
        enabled: isPostOnBehalfAvailable.value,
      },

      {
        title: t('appMenu.addToGroup'),
        subTitle: null,
        icon: AppIconsEnum.Users,
        type: SettingsTypeEnum.AddToGroup,
        params: {
          value: allowAddMeToGroups.value,
          options: [
            {
              title: t('on'),
              value: true,
            },
            {
              title: t('off'),
              value: false,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(2),
        enabled:
          false /** @todo temporary disabled #1882; NOTE: when will be ready, bring back "SettingsTypeEnum.AboutApp" size 2 */,
      },

      /**
       * @link https://gitlab.united-grid.com/intra/intra-ionic/-/issues/2280
       */
      {
        title: t('settings.docsInNewWindow'),
        subTitle: null,
        icon: AppIconsEnum.ExternalLink,
        type: SettingsTypeEnum.DocsInNewWindow,
        params: {
          value: appStore.docsInNewWindow,
          options: [
            {
              title: t('on'),
              value: true,
            },
            {
              title: t('off'),
              value: false,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(2),
        enabled: __DEV__ && !isNativeMobile && !isPlatform('electron'),
      },

      {
        title: t('appMenu.interface'),
        subTitle: null,
        icon: AppIconsEnum.Title,
        type: SettingsTypeEnum.InterfaceSize,
        params: {
          value: interfaceSize.value,
          options: [
            {
              title: t('settings.interfaceSize.small'),
              value: InterfaceSizeAppEnum.Small,
            },
            {
              title: t('settings.interfaceSize.middle'),
              value: InterfaceSizeAppEnum.Middle,
            },
            {
              title: t('settings.interfaceSize.large'),
              value: InterfaceSizeAppEnum.Large,
            },
          ],
        },
        textContent: '',
        actions: null,
        size: getItemSize(1),
        enabled: isNativeMobile,
      },

      {
        title: t('cache.clear'),
        subTitle: t('cache.clearSubTitle', {
          generalSize: (calculateAllStoresSize() / (1024 * 1024)).toFixed(2),
          imageSize: (useImages().size / (1024 * 1024)).toFixed(2),
        }),
        icon: AppIconsEnum.Remove,
        type: SettingsTypeEnum.Cache,
        params: null,
        textContent: null,
        actions: {
          leftBtn: {
            title: '',
            action: SettingsActionEnum.None,
          },
          rightBtn: {
            title: t('cache.clear'),
            action: SettingsActionEnum.ClearCache,
          },
        },
        size: getItemSize(2),
        enabled: true,
      },

      {
        title: t('appMenu.usageRules'),
        subTitle: currentNetwork.value.title,
        icon: AppIconsEnum.List,
        type: SettingsTypeEnum.NetworkRules,
        params: null,
        textContent: null,
        actions: {
          leftBtn: {
            title: t('support.title'),
            action: SettingsActionEnum.Support,
          },
          rightBtn: {
            title: t('open'),
            action: SettingsActionEnum.OpenUsageRules,
          },
        },
        size: getItemSize(2),
        enabled: true,
      },

      {
        title: null,
        subTitle: null,
        icon: AppIconsEnum.Appearance,
        type: SettingsTypeEnum.AboutApp,
        params: null,
        textContent: null,
        actions: null,
        size: getItemSize(4),
        enabled: true,
      },
    ];

    return items.filter((n) => n.enabled);
  };

  const settingParamsAction = async (
    type: SettingsTypeEnum,
    value: boolean | ThemeAppEnum | PagesBackgroundEnum | AppMenuLanguagesEnum | SendKeyEnum | InterfaceSizeAppEnum,
    currentNetworkId: string
  ): Promise<void> => {
    const appStore = useAppStore();
    const userStore = useUserStore();

    switch (type) {
      case SettingsTypeEnum.Theme:
        {
          const data = value as ThemeAppEnum;
          appStore.setTheme(data);
          await userStore.setTheme(data);
        }
        return;

      case SettingsTypeEnum.Background:
        {
          const data = value as PagesBackgroundEnum;
          appStore.setBackground(data);
          await userStore.pagesBackground(value === PagesBackgroundEnum.CustomImage ? true : false);
        }
        return;

      case SettingsTypeEnum.Sound:
        {
          const data = value as boolean;
          appStore.$patch({
            localNotifications: data,
          });
          await userStore.appSound(data);
        }
        return;

      case SettingsTypeEnum.PostsOnBehalf:
        {
          const data = value as boolean;
          appStore.$patch({
            canPostOnBehalfMe: data,
          });
          await userStore.setPostOnBehalfOption(data);
        }
        return;

      case SettingsTypeEnum.AddToGroup:
        {
          const data = value as boolean;
          await userStore.setAddToGroupOption(data);
        }
        return;

      case SettingsTypeEnum.DocsInNewWindow:
        {
          const data = value as boolean;
          appStore.setDocsInNewWindow(data);
        }
        return;

      case SettingsTypeEnum.Lang:
        {
          const data = value as AppMenuLanguagesEnum;
          axiosInstance.defaults.headers.common['Accept-Language'] = data;
          const companyResources = await appStore.getCompanyResources();
          await loadLocaleMessages(data, companyResources, currentNetworkId);
          appStore.$patch({
            locale: data,
          });
          await userStore.setLanguage(data);
        }
        return;

      case SettingsTypeEnum.Sending:
        {
          const data = value as SendKeyEnum;
          appStore.$patch({
            sendKey: data,
          });
          await userStore.setSendMode(data);
        }
        return;

      case SettingsTypeEnum.InterfaceSize: {
        const data = value as InterfaceSizeAppEnum;
        if (isNativeMobile) {
          appStore.$patch({
            interfaceSize: data,
          });
          switch (data) {
            case InterfaceSizeAppEnum.Small:
              await TextZoom.set({ value: 0.8 });
              break;
            case InterfaceSizeAppEnum.Middle:
              await TextZoom.set({ value: 1.0 });
              break;
            case InterfaceSizeAppEnum.Large:
              await TextZoom.set({ value: 1.2 });
              break;
          }
        }
      }

      default:
        return;
    }
  };

  const settingButtonsAction = async (action: SettingsActionEnum): Promise<void> => {
    switch (action) {
      case SettingsActionEnum.ClearCache:
        {
          await clearCache();
        }
        return;

      case SettingsActionEnum.Support:
        {
          await openSupportModal(null);
        }
        return;

      case SettingsActionEnum.OpenUsageRules:
        {
          await router.push({ name: ROUTES_NAME.USAGE_RULES });
        }
        return;

      case SettingsActionEnum.OpenNotifications:
        {
          await openUserSubscribeSettingsModal();
        }
        return;

      default:
        return;
    }
  };

  const showAppInfo = async () => {
    try {
      const appVersion = useAppStore().appVersion;
      const appBuild = useAppStore().appBuild;

      const appInfo = reactive([
        { name: 'Version', value: appVersion, type: AppInfoListEnum.Version },
        { name: 'Build', value: appBuild, type: AppInfoListEnum.Build },
        {
          name: 'Commit hash',
          value: import.meta.env.VITE_APP_COMMIT_HASH,
          type: AppInfoListEnum.CommitHash,
        },
        {
          name: 'Updated',
          value:
            import.meta.env.VITE_APP_COMMIT_TIME === 'VITE_APP_COMMIT_TIME_PLACEHOLDER'
              ? import.meta.env.VITE_APP_COMMIT_TIME
              : DateHelper.formatToUserTimezone(import.meta.env.VITE_APP_COMMIT_TIME),
          // value: DateHelper.formatToUserTimezone(new Date().toISOString()),
          type: AppInfoListEnum.CommitTime,
        },
        {
          name: 'Core',
          value: `https://${useAuthStore().host}`,
          type: AppInfoListEnum.Core,
        },
        { name: 'Company', value: `https://${useAuthStore().host}`, type: AppInfoListEnum.Company },
        {
          name: 'SignalR',
          value: useAuthStore().webSocket,
          type: AppInfoListEnum.SignalR,
        },
        {
          name: 'API',
          value: import.meta.env.VITE_API_URL,
          type: AppInfoListEnum.Api,
        },
        {
          name: 'Vue',
          value: JSON.stringify(packageJson.dependencies.vue),
          type: AppInfoListEnum.Vue,
        },
        {
          name: 'Ionic',
          value: JSON.stringify(packageJson.dependencies['@ionic/vue']),
          type: AppInfoListEnum.Ionic,
        },
        {
          name: 'Platforms',
          value: getPlatforms(),
          type: AppInfoListEnum.Capacitor,
        },
        {
          name: 'Platform Capacitor',
          value: Capacitor.getPlatform(),
          type: AppInfoListEnum.Capacitor,
        },
        {
          name: 'Is Native',
          value: Capacitor.isNativePlatform(),
          type: AppInfoListEnum.Capacitor,
        },
      ]);

      if (isNativeMobile) {
        const buildInfo = await App.getInfo();
        appInfo[0].value = buildInfo.version;
        appInfo[1].value = buildInfo.build;
      }

      let messageBody = '';
      appInfo.forEach((element) => {
        if (element.value !== null) {
          if (
            isNativeMobile &&
            (element.type === AppInfoListEnum.CommitHash || element.type === AppInfoListEnum.CommitTime)
          )
            return;

          const line = '<b>' + element.name + ': </b>' + element.value + '<br>';
          return (messageBody += line);
        }
      });

      const alert = await alertController.create({
        header: t('appMenu.aboutApp'),
        message: messageBody,
        buttons: [
          {
            text: t('close'),
            role: 'cancel',
            cssClass: 'custom-alert-buttons',
          },
        ],
      });

      await alert.present();
    } catch (error) {
      console.error('[VERBOSE] ShowAppInfo2 - Error occurred:', error);
    }
  };

  //NOTE: settings for notifications
  const notificationsSettings = () => {
    const userStore = useUserStore();
    const currentUser = userStore.current;
    const currentUserRole = currentUser?.roleId ?? 0;
    const subscribeSettings = currentUser?.subscribeSettings;

    const _createNotificationItem = <T extends keyof UserSubscribeSettingsRequestModel>(
      title: string,
      modelName: T,
      checked: boolean | undefined,
      enabled: boolean
    ): NotificationsSettingsItemModel => {
      return { title, modelName, checked, enabled };
    };

    const notificationsForAdmins: NotificationsSettingsModel = {
      title: t('settings.notifications.category.notificationsForAdmins'),
      items: [
        _createNotificationItem(
          t('settings.notifications.subscribeComplainMessage'),
          'subscribeComplainMessage',
          subscribeSettings?.subscribeComplainMessage,
          currentUserRole >= UserRoleEnum.Moderator
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeRequestRegistrationToNetwork'),
          'subscribeRequestRegistrationToNetwork',
          subscribeSettings?.subscribeRequestRegistrationToNetwork,
          currentUserRole >= UserRoleEnum.Moderator
        ),
      ].filter((n) => n.enabled),
    };

    const chooseWhereToSend: NotificationsSettingsModel = {
      title: t('settings.notifications.category.chooseWhereToSend'),
      items: [
        _createNotificationItem(
          t('settings.notifications.agreeReceiveEmailNotifications'),
          'enableEmailNotifications',
          currentUser?.enableEmailNotifications,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.agreeReceiveNotificationsInApp'),
          'enableMobileNotifications',
          currentUser?.enableMobileNotifications,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.agreeReceiveNotificationsInSite'),
          'enableSiteNotifications',
          currentUser?.enableSiteNotifications,
          true
        ),
      ].filter((n) => n.enabled),
    };

    const notifyMeIf: NotificationsSettingsModel = {
      title: t('settings.notifications.category.notifyMeIf') + `...`,
      items: [
        _createNotificationItem(
          t('settings.notifications.subscribeMentionMeInMessage', { n: '@' }),
          'subscribeMentionMeInMessage',
          subscribeSettings?.subscribeMentionMeInMessage,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeReceiveReplies'),
          'subscribeReceiveReplies',
          subscribeSettings?.subscribeReceiveReplies,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeReceivePrivateMessage'),
          'subscribeReceivePrivateMessage',
          subscribeSettings?.subscribeReceivePrivateMessage,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeInvitedToGroup'),
          'subscribeInvitedToGroup',
          subscribeSettings?.subscribeInvitedToGroup,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeGetNewFollowers'),
          'subscribeGetNewFollowers',
          subscribeSettings?.subscribeGetNewFollowers,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeLikedPostedMessage'),
          'subscribeLikedPostedMessage',
          subscribeSettings?.subscribeLikedPostedMessage,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeNewMessageInMyFollowers'),
          'subscribeNewMessageInMyFollowers',
          subscribeSettings?.subscribeNewMessageInMyFollowers,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeNewMessageInAllSite'),
          'subscribeNewMessageInAllSite',
          subscribeSettings?.subscribeNewMessageInAllSite,
          true
        ),
        _createNotificationItem(
          t('settings.notifications.subscribeAnnouncementsNotification'),
          'subscribeAnnouncementsNotification',
          subscribeSettings?.subscribeAnnouncementsNotification,
          false //NOTE: Disasbled for all by @siaarzh
        ),
      ].filter((n) => n.enabled),
    };

    const activityTypeOptions = [
      {
        title: t('settings.notifications.activityType.never'),
        value: ActivityTypeEnum.Never,
      },
      {
        title: t('settings.notifications.activityType.daily'),
        value: ActivityTypeEnum.Daily,
      },
      {
        title: t('settings.notifications.activityType.weekly'),
        value: ActivityTypeEnum.Weekly,
      },
    ];

    const activityType: FormSelectDataModel = {
      title: t('settings.notifications.activityType.title'),
      value:
        activityTypeOptions.find((option) => option.value === subscribeSettings?.activityType)?.value ||
        ActivityTypeEnum.Never,
      options: activityTypeOptions,
    };

    const groups = {
      title: t('settings.notifications.category.newsOfTheGroups'),
      items: subscribeSettings?.groupsNotifications ?? [],
    };

    return {
      notificationsForAdmins,
      chooseWhereToSend,
      notifyMeIf,
      activityType,
      groups,
    };
  };

  return {
    getSettingsItems,
    settingParamsAction,
    settingButtonsAction,
    showAppInfo,
    notificationsSettings,
  };
};
